import { Component, inject, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { ErrorService, ErrorStatsResponse, GameError } from '../../../service/error.service';
import { timer, Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { Chart, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-luxon';
import { IconComponent } from "../../../helper/icon/icon.component";

Chart.register(...registerables, zoomPlugin);

@Component({
  selector: 'app-errors',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatIconModule,
    MatToolbarModule,
    MatTooltipModule,
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    IconComponent
],
  templateUrl: './errors.component.html',
  styleUrl: './errors.component.less'
})
export class ErrorsComponent implements OnInit, OnDestroy, AfterViewInit {
  private errorService = inject(ErrorService);
  
  // Data
  data: ErrorStatsResponse | null = null;
  isLoading = true;
  private refreshSub?: Subscription;
  
  // Filters
  filters: {
    world: string;
    version: string;
    platform: string;
    from: any;
    to: any;
  } = {
    world: '',
    version: '',
    platform: '',
    from: '',
    to: ''
  };
  
  // Pagination
  totalErrors = 0;
  pageSize = 50;
  pageIndex = 0;
  
  // Table
  displayedColumns: string[] = ['dt', 'world', 'error_code', 'version', 'platform'];
  
  // Graph State
  @ViewChild('errorChart') chartCanvas?: ElementRef<HTMLCanvasElement>;
  chart?: Chart;
  
  private filterSubject = new Subject<void>();

  ngOnInit() {
    this.refreshSub = timer(0, 60000).subscribe(() => {
      this.fetchData();
    });

    this.filterSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.pageIndex = 0;
      this.fetchData();
    });
  }

  ngAfterViewInit() {
    this.initChart();
  }

  ngOnDestroy() {
    this.refreshSub?.unsubscribe();
    this.chart?.destroy();
  }

  initChart() {
    if (!this.chartCanvas) return;

    this.chart = new Chart(this.chartCanvas.nativeElement, {
      type: 'line',
      data: {
        labels: [],
        datasets: [{
          label: 'Errors',
          data: [],
          borderColor: '#42a5f5',
          backgroundColor: 'rgba(66, 165, 245, 0.2)',
          fill: true,
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 5
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'MMM d, HH:mm'
              }
            },
            grid: {
              display: false
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.7)'
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
              color: 'rgba(255, 255, 255, 0.7)'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        plugins: {
          tooltip: {
            mode: 'index',
            intersect: false
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: false
              },
              pinch: {
                enabled: false
              },
              mode: 'x'
            },
            pan: {
              enabled: false,
              mode: 'x'
            }
          }
        }
      }
    });
  }

  fetchData() {
    this.isLoading = true;
    
    // Default to last 7 days if no range specified
    if (!this.filters.from && !this.filters.to) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      this.filters.from = sevenDaysAgo.toISOString();
      this.filters.to = new Date().toISOString();
    }

    // Ensure dates are ISO strings for the API
    const fromStr = this.filters.from instanceof Date ? this.filters.from.toISOString() : this.filters.from;
    const toStr = this.filters.to instanceof Date ? this.filters.to.toISOString() : this.filters.to;

    const params = {
      ...this.filters,
      from: fromStr,
      to: toStr,
      page: this.pageIndex,
      page_size: this.pageSize
    };
    
    this.errorService.getErrors(params).subscribe({
      next: (res) => {
        this.data = res;
        this.totalErrors = res.total;
        this.isLoading = false;
        this.updateChart();
      },
      error: (err) => {
        console.error('Failed to fetch errors', err);
        this.isLoading = false;
      }
    });
  }

  updateChart() {
    if (!this.chart || !this.data) return;

    // Use {x, y} format for better time scale support
    const chartData: any[] = this.data.graph.map(g => ({
      x: g.label,
      y: g.count
    }));

    this.chart.data.datasets[0].data = chartData;
    
    // Ensure the chart has rendered at least once to get chartArea
    this.chart.update('none'); 

    const chartArea = this.chart.chartArea;
    const yScale = this.chart.scales['y'];
    const ctx = this.chartCanvas?.nativeElement.getContext('2d');

    if (chartArea && yScale && ctx) {
      // Create gradient based on Y-axis values
      // We want 0 -> Green, 25 -> Yellow, 50+ -> Red
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      
      // Calculate stops relative to the chart area
      // yScale.getPixelForValue(v) returns the Y coordinate for value v
      const p0 = yScale.getPixelForValue(0);
      const p25 = yScale.getPixelForValue(25);
      const p50 = yScale.getPixelForValue(50);

      // Convert pixel positions to 0-1 range for the gradient
      const getStop = (p: number) => {
        const stop = (p - chartArea.bottom) / (chartArea.top - chartArea.bottom);
        return Math.max(0, Math.min(1, stop));
      };

      // Note: Chart.js Y coordinates are inverted (top is 0)
      // So use a simpler approach: map values directly if possible, or use the pixels
      
      // More reliable way for CanvasGradient: use the actual pixel values if we can
      // But addColorStop takes 0-1.
      // 0 is bottom (high Y pixel), 1 is top (low Y pixel)
      
      gradient.addColorStop(0, '#4caf50'); // Green at bottom
      
      const stop25 = (chartArea.bottom - p25) / (chartArea.bottom - chartArea.top);
      if (stop25 > 0 && stop25 < 1) {
        gradient.addColorStop(stop25, '#ffeb3b'); // Yellow at 25
      } else if (stop25 >= 1) {
        // If 25 is above the current top, we can't add an intermediate stop easily that makes it yellow at exactly 25
        // but the gradient will naturally transition towards it.
      }

      const stop50 = (chartArea.bottom - p50) / (chartArea.bottom - chartArea.top);
      if (stop50 > 0 && stop50 < 1) {
        gradient.addColorStop(stop50, '#f44336'); // Red at 50
      } else if (stop50 >= 1) {
        // gradient remains red at top
      }
      
      // If the max value is less than 50, the top will be whatever color is at the top of the range
      // The requirement says "ensuring a 25 always shows up yellow"
      
      this.chart.data.datasets[0].borderColor = gradient;
      this.chart.data.datasets[0].backgroundColor = gradient;
    }

    // Update time unit based on range
    const from = new Date(this.filters.from).getTime();
    const to = new Date(this.filters.to).getTime();
    const diffDays = (to - from) / (1000 * 60 * 60 * 24);

    const xScale: any = this.chart.options.scales?.['x'];
    if (xScale && xScale.time) {
      if (diffDays <= 2) {
        xScale.time.unit = 'hour';
      } else if (diffDays <= 30) {
        xScale.time.unit = 'day';
      } else {
        xScale.time.unit = 'month';
      }
    }

    this.chart.update();
  }

  onFilterChange() {
    this.filterSubject.next();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
  }

  getPlatformIcon(platform: string): string {
    switch (platform.toLowerCase()) {
      case 'pc': return 'desktop_windows';
      case 'android': return 'head_mounted_device';
      case 'ios': return 'mobile_3';
      default: return 'help_outline';
    }
  }

  getErrorLevel(code: string): string {
    if (code.startsWith('4')) return 'warn';
    if (code.startsWith('5')) return 'error';
    return 'info';
  }
}
